import { Injectable, computed, effect, inject, untracked } from '@angular/core';
import { AuthStore, SyncedCollection, combineSyncStatus } from '@huddle-sports-platform/app-core';
import { isMembershipOnSale } from '@huddle-sports-platform/ui';
import {
  currentSeason,
  openSeason,
  sortSeasons,
} from '@huddle-sports-platform/schema/client';
import {
  memberSchema,
  clubEventSchema,
  clubSchema,
  clubUserSchema,
  eventTicketSchema,
  lotteryDrawSchema,
  lotteryEntrySchema,
  lotterySchema,
  membershipPlanSchema,
  seasonSchema,
  paymentRequestSchema,
  squadSchema,
  registrationSchema,
  transactionSchema,
  type Club,
  type Member,
  type ClubEvent,
  type ClubUser,
  type EventTicket,
  type Lottery,
  type LotteryDraw,
  type LotteryEntry,
  type SeasonPolicy,
  type MembershipPlan,
  type Season,
  type PaymentRequest,
  type Squad,
  type Registration,
  type Transaction,
} from '@huddle-sports-platform/schema';
import { registrationKey } from '@huddle-sports-platform/schema/meta';
import { collection, documentId, limit, query, where } from 'firebase/firestore';

import { db } from '../core/firebase';
import { AccountStore } from './account-store';
import { ClubBrandStore } from './club-brand';

/** Firestore's ceiling on an `in` filter - the footprint slices to fit. */
/** How long the member footprint may hold the boot veil before the app
 *  comes up regardless (see settled()). */
const WARM_TIMEOUT_MS = 8_000;

const IN_LIMIT = 30;

/** Bounded like the admin twin's - a member screen never pages draws. */
const DRAWS_LIMIT = 120;
const LOTTERY_ENTRIES_LIMIT = 300;

/** A registration with its `{planId}_{memberId}` key unpacked. */
export interface MemberRegistrationEntry {
  readonly seasonId: string;
  readonly planId: string;
  readonly memberId: string;
  readonly registration: Registration;
}

/**
 * THE MEMBER'S footprint: everything this login can see at this club,
 * live, warmed by DataInit before the first page paints.
 *
 * The admin twin is `ClubDataStore` (the whole club); this is the same idea
 * scoped to one person - and the scoping is not a courtesy, it is what
 * makes the reads legal. The rules are GATES, NOT FILTERS: a query that
 * would return one doc this login may not read fails ENTIRELY, so every
 * query here carries the constraint its rule checks (kit
 * docs/data/security.md). Three shapes appear:
 *
 * - **Club-wide, member-readable** (`isUserOf()`): plans, squads.
 *   No constraint needed - belonging to the club is the whole rule.
 * - **Field-scoped**: tickets, transactions and paymentRequests carry a
 *   `memberId`, so they are read `where('memberId', 'in', mine)`.
 * - **Key-scoped**: a registration's owner lives in its `{planId}_{memberId}`
 *   DOC KEY and nowhere in the body, so those are read by building the keys
 *   and asking for them by id.
 *
 * The two key-scoped and field-scoped tiers need to know WHO this login is
 * at this club (their `access/{uid}` doc's memberId, plus anyone they pay for
 * via `account.manages`), so they warm a beat after the rest. {@link start}
 * drives that chain imperatively - guards run before Angular flushes root
 * effects, and a store whose first load is effect-driven deadlocks the
 * router (CLAUDE.md, 'the guard/effect deadlock'). The effects below only
 * catch LATER changes: a new membership going on sale, a child being added.
 *
 * Deliberately absent: `installments` and `installmentPlans`. Their keys are
 * `{planId}_{memberId}_{planSeq}[_{seq}]` - the sequence numbers are not
 * knowable client-side, so they cannot be enumerated into an `in` filter,
 * and no member surface reads them yet. The day one does, it takes a
 * documentId RANGE listener per (plan, user) - the prefix is provable.
 */
@Injectable({ providedIn: 'root' })
export class MemberDataStore {
  private readonly auth = inject(AuthStore);
  private readonly clubBrand = inject(ClubBrandStore);
  private readonly account = inject(AccountStore);

  // ------------------------------------------------- tier A: club-readable
  /** The club doc itself - `payments.chargesEnabled` and the fee-terms
   *  mirror, which the payables view quotes before anyone taps Pay. */
  private readonly clubSync = new SyncedCollection<Club>(clubSchema, 'member-data:club');
  /** My LOGIN at this club - what the rules read for caps, and what says
   *  whether I am a member of it at all. Replaced the access mirror. */
  private readonly accessSync = new SyncedCollection<ClubUser>(
    clubUserSchema,
    'member-data:login',
  );
  private readonly seasonsSync = new SyncedCollection<Season>(
    seasonSchema,
    'member-data:seasons',
  );
  private readonly plansSync = new SyncedCollection<MembershipPlan>(
    membershipPlanSchema,
    'member-data:membershipPlans',
  );
  private readonly squadsSync = new SyncedCollection<Squad>(squadSchema, 'member-data:squads');
  private readonly eventsSync = new SyncedCollection<ClubEvent>(
    clubEventSchema,
    'member-data:events',
  );
  /** The club lottery: what is RUNNING (the rule proves the status
   *  constraint), and every draw from open onwards - results included. */
  private readonly lotteriesSync = new SyncedCollection<Lottery>(
    lotterySchema,
    'member-data:lotteries',
  );
  private readonly lotteryDrawsSync = new SyncedCollection<LotteryDraw>(
    lotteryDrawSchema,
    'member-data:lotteryDraws',
  );

  // ------------------------------------------------------- tier B: mine
  /** My own roster doc, by the `linkedUid` the rules key ownership on. */
  private readonly meSync = new SyncedCollection<Member>(memberSchema, 'member-data:me');
  /** The people I pay for - a separate listener so a refusal here cannot
   *  take my own record down with it. */
  private readonly managedSync = new SyncedCollection<Member>(
    memberSchema,
    'member-data:managed',
  );
  private readonly ticketsSync = new SyncedCollection<EventTicket>(
    eventTicketSchema,
    'member-data:tickets',
  );
  private readonly requestsSync = new SyncedCollection<PaymentRequest>(
    paymentRequestSchema,
    'member-data:paymentRequests',
  );
  private readonly transactionsSync = new SyncedCollection<Transaction>(
    transactionSchema,
    'member-data:transactions',
  );
  private readonly lotteryEntriesSync = new SyncedCollection<LotteryEntry>(
    lotteryEntrySchema,
    'member-data:lotteryEntries',
  );

  // -------------------------------------------------- tier C: key-scoped
  private readonly registrationsSync = new SyncedCollection<Registration>(
    registrationSchema,
    'member-data:registrations',
  );

  private readonly footprint = [
    this.clubSync,
    this.accessSync,
    this.seasonsSync,
    this.plansSync,
    this.squadsSync,
    this.eventsSync,
    this.lotteriesSync,
    this.lotteryDrawsSync,
    this.meSync,
    this.managedSync,
    this.ticketsSync,
    this.requestsSync,
    this.transactionsSync,
    this.lotteryEntriesSync,
    this.registrationsSync,
  ] as const;

  private readonly status = combineSyncStatus([...this.footprint]);
  readonly warming = this.status.warming;
  readonly failed = this.status.failed;
  readonly live = this.status.live;
  readonly lastSyncAt = this.status.lastSyncAt;

  /** The `uid|clubId` the current warm answers for - start() is idempotent. */
  private warmedFor: string | null = null;
  /** The scope keys tiers B and C were last warmed with, so a re-run that
   *  changes nothing does not churn the listeners. */
  private mineKey: string | null = null;
  private subsKey: string | null = null;

  constructor() {
    // Sign-out drops the footprint; the next session warms afresh.
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      untracked(() => {
        if (uid === null) this.detach();
      });
    });

    // LATER changes only - the first warm is start()'s imperative chain.
    // A child added to the account, or the access doc landing after a
    // fresh sign-up's auto-join, re-scopes the personal tiers.
    effect(() => {
      const ids = this.myUserIds();
      const seasonIds = this.payableSeasonIds();
      untracked(() => {
        if (this.warmedFor === null) return;
        this.warmMine(ids);
        this.warmSubscriptions(ids, seasonIds);
      });
    });
  }

  // ------------------------------------------------------------- warming

  /**
   * DataInit's entry point: warm the member footprint and resolve once it
   * is live. Settles on failure too - a loader that never resolves holds
   * the boot veil forever (see DataInit's contract).
   */
  async whenLoaded(): Promise<void> {
    if (!this.auth.user()) return;
    // On the web the club comes from the address, resolved asynchronously.
    await this.whenBranded();
    this.start();
    // The personal tiers need my club memberId (the access doc) and the
    // people I pay for (the account doc), so they follow the first wave.
    await this.settled();
    await this.account.whenLoaded();
    const ids = this.myUserIds();
    this.warmMine(ids);
    this.warmSubscriptions(ids, this.payableSeasonIds());
    await this.settled();
  }

  /** Attach the club-readable tier - imperative and idempotent. */
  start(): void {
    const uid = this.auth.user()?.uid ?? null;
    const clubId = this.clubBrand.brand()?.clubId ?? null;
    if (!uid || !clubId) return;
    const key = `${uid}|${clubId}`;
    if (this.warmedFor === key) return;
    this.warmedFor = key;
    const sub = (name: string) => collection(db(), 'clubs', clubId, name);

    // The club doc, by id - the rule (isUserOf) is path-based, so the
    // one-doc query proves itself.
    this.clubSync.warm(query(collection(db(), 'clubs'), where(documentId(), '==', clubId)));
    // Who I am at this club - one doc, read by id (the rule is isSelf).
    this.accessSync.warm(query(sub('users'), where(documentId(), '==', uid)));
    // Belonging to the club IS the rule for these three.
    this.seasonsSync.warm(sub('seasons'));
    this.plansSync.warm(sub('membershipPlans'));
    this.squadsSync.warm(sub('squads'));
    // Drafts are the committee's business; a member sees what is on sale.
    this.eventsSync.warm(query(sub('events'), where('status', '==', 'published')));
    // The lottery: what is RUNNING (the rule proves the status constraint
    // against the query), and every draw from open onwards - a scheduled
    // draw is not yet news, and the rule says so.
    this.lotteriesSync.warm(query(sub('lotteries'), where('status', '==', 'active')));
    this.lotteryDrawsSync.warm(
      query(
        sub('lotteryDraws'),
        where('status', 'in', ['open', 'closed', 'drawn']),
        limit(DRAWS_LIMIT),
      ),
    );
  }

  /** Re-attach the whole footprint (a page's retry affordance). */
  retry(): void {
    const warmedFor = this.warmedFor;
    this.detach();
    if (warmedFor === null) return;
    this.start();
    const ids = this.myUserIds();
    this.warmMine(ids);
    this.warmSubscriptions(ids, this.payableSeasonIds());
  }

  private detach(): void {
    this.warmedFor = null;
    this.mineKey = null;
    this.subsKey = null;
    for (const sync of this.footprint) sync.detach();
  }

  /** The field-scoped tier: everything carrying a `memberId` that is mine. */
  private warmMine(memberIds: readonly string[]): void {
    const clubId = this.clubBrand.brand()?.clubId ?? null;
    const uid = this.auth.user()?.uid ?? null;
    if (!clubId || !uid) return;
    const key = `${clubId}|${memberIds.join(',')}`;
    if (this.mineKey === key) return;
    this.mineKey = key;
    const sub = (name: string) => collection(db(), 'clubs', clubId, name);

    // My own roster doc answers to `linkedUid`, so it needs no id at all -
    // it warms even before the access doc has landed.
    this.meSync.warm(query(sub('members'), where('linkedUid', '==', uid)));

    if (memberIds.length === 0) {
      this.managedSync.detach();
      this.ticketsSync.detach();
      this.requestsSync.detach();
      this.transactionsSync.detach();
      this.lotteryEntriesSync.detach();
      return;
    }
    const ids = memberIds.slice(0, IN_LIMIT);
    this.managedSync.warm(query(sub('members'), where(documentId(), 'in', ids)));
    this.ticketsSync.warm(query(sub('tickets'), where('memberId', 'in', ids)));
    // My lottery lines (and my family's) - ownership is the memberId FIELD
    // (readsOwnOrBilled), same shape as tickets.
    this.lotteryEntriesSync.warm(
      query(sub('lotteryEntries'), where('memberId', 'in', ids), limit(LOTTERY_ENTRIES_LIMIT)),
    );
    // BOTH constraints are load-bearing. The rule reads
    // `audience == 'member' && readsOwnOrBilled(memberId)`, and a list is
    // proven against the QUERY, not the results: leaving `audience`
    // unconstrained is refused outright, even though no sponsor request
    // carries a memberId to match in the first place.
    this.requestsSync.warm(
      query(sub('paymentRequests'), where('audience', '==', 'member'), where('memberId', 'in', ids)),
    );
    // No orderBy: a member's own ledger is small, and sorting in a computed
    // keeps this a single-field query that needs no composite index.
    this.transactionsSync.warm(query(sub('transactions'), where('memberId', 'in', ids)));
  }

  /** The key-scoped tier: registrations, by the keys they must be asked for. */
  private warmSubscriptions(memberIds: readonly string[], seasonIds: readonly string[]): void {
    const clubId = this.clubBrand.brand()?.clubId ?? null;
    if (!clubId) return;
    const keys: string[] = [];
    for (const seasonId of seasonIds) {
      for (const memberId of memberIds) keys.push(registrationKey(seasonId, memberId));
    }
    const scope = keys.slice(0, IN_LIMIT);
    const key = `${clubId}|${scope.join(',')}`;
    if (this.subsKey === key) return;
    this.subsKey = key;
    if (scope.length === 0) {
      this.registrationsSync.detach();
      return;
    }
    this.registrationsSync.warm(
      query(collection(db(), 'clubs', clubId, 'registrations'), where(documentId(), 'in', scope)),
    );
  }

  private whenBranded(): Promise<void> {
    if (this.clubBrand.settled()) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setInterval(() => {
        if (this.clubBrand.settled()) {
          clearInterval(timer);
          resolve();
        }
      }, 25);
    });
  }

  /**
   * Every attached listener's first server snapshot, or a failure - CAPPED.
   *
   * The cap is the point: a listener that is attached and healthy but never
   * hears back from the server (no route to Firestore, a hung connection)
   * is neither `warming` resolved nor `failed`, so an uncapped wait holds
   * the boot veil for ever. The same rule the crest already follows: a
   * missing crest is cosmetic, a launch that never finishes is not
   * (core/crest.ts). Past the cap the app comes up on whatever is in hand,
   * and the listeners keep trying underneath.
   */
  private settled(): Promise<void> {
    return new Promise((resolve) => {
      const deadline = Date.now() + WARM_TIMEOUT_MS;
      const timer = setInterval(() => {
        if (this.failed() || !this.warming() || Date.now() >= deadline) {
          clearInterval(timer);
          resolve();
        }
      }, 25);
    });
  }

  // --------------------------------------------------------------- views

  /** The club doc - the payments mirror lives on it. */
  readonly club = computed<Club | undefined>(() => [...this.clubSync.docs().values()][0]);

  /** The club's leading name for member surfaces: what members call it,
   *  the full name, then the branded app's own name - ONE fallback chain
   *  (home, welcome and friends all read this). */
  readonly clubName = computed<string>(
    () =>
      this.club()?.shortName ??
      this.club()?.name ??
      this.clubBrand.brand()?.appName ??
      'Your club',
  );

  /** My `access/{uid}` doc - caps, roles and my club memberId. */
  readonly access = computed<ClubUser | undefined>(
    () => [...this.accessSync.docs().values()][0],
  );

  /** My own club memberId, once the access doc has answered. */
  readonly myUserId = computed<string | null>(() => this.access()?.memberId ?? null);

  /**
   * Everyone whose bills land on this login: me, plus the people my
   * account pays for at this club (the parent-and-child tie).
   */
  readonly myUserIds = computed<readonly string[]>(() => {
    const clubId = this.clubBrand.brand()?.clubId ?? null;
    const mine = this.myUserId();
    const managed = clubId
      ? (this.account.account()?.manages.find((entry) => entry.clubId === clubId)?.memberIds ?? [])
      : [];
    return [...new Set([...(mine ? [mine] : []), ...managed])];
  });

  /** The memberships worth paying for - a finished membership's debts are
   *  the club's to chase, not a button on a member's phone. What used to
   *  be "the open seasons" is now the plans still on sale. */
  /**
   * The seasons worth reading a registration for - every season the club
   * has run, newest first and capped, because a member's OWN history is
   * what the payments and home surfaces show. Keys are
   * `{seasonId}_{memberId}`, so scoping by season is what scopes the
   * listener.
   */
  private readonly payableSeasonIds = computed<readonly string[]>(() =>
    this.seasons()
      .slice(0, 4)
      .map((season) => season.seasonId),
  );

  /** My own roster record at this club. */
  readonly me = computed<Member | undefined>(() => [...this.meSync.docs().values()][0]);

  /** Me and everyone I pay for, by club memberId. */
  readonly people = computed<ReadonlyMap<string, Member>>(() => {
    const people = new Map<string, Member>(this.managedSync.docs());
    for (const [memberId, user] of this.meSync.docs()) people.set(memberId, user);
    return people;
  });

  /** A person's display name, for a payer looking at several. */
  nameFor(memberId: string): string {
    const user = this.people().get(memberId);
    return user ? `${user.firstName} ${user.lastName}` : 'Member';
  }

  readonly squadsById = this.squadsSync.docs;
  /** Keyed by the planId, which IS the doc key. */
  readonly plansByKey = this.plansSync.docs;

  /** The memberships on sale right now (the same derivation the admin
   *  side shares; see ClubDataStore.livePlans). */
  readonly livePlans = computed(() => {
    const now = Date.now();
    const open = this.openSeason();
    return Array.from(this.plansByKey(), ([planId, plan]) => ({ planId, plan })).filter(
      ({ plan }) => isMembershipOnSale(plan, open, now),
    );
  });

  /** The club's seasons, newest first, and the two derivations every
   *  member surface needs: what is running and what is on sale. */
  readonly seasons = computed<readonly Season[]>(() =>
    sortSeasons([...this.seasonsSync.docs().values()]),
  );
  readonly currentSeason = computed(() =>
    currentSeason(this.seasons(), new Date().toISOString().slice(0, 10)),
  );
  readonly openSeason = computed(() => openSeason(this.seasons()));

  /** The MEMBERSHIP TERMS the open season is sold under - what a buyer
   *  must read and accept before registering, and the version they
   *  accept. They live ON the season, so there is nothing to look up. */
  readonly openSeasonMembershipTerms = computed<SeasonPolicy | null>(
    () => this.openSeason()?.membershipTerms ?? null,
  );

  /** Published events, soonest first. */
  readonly events = computed(() =>
    Array.from(this.eventsSync.docs(), ([eventId, event]) => ({ eventId, event })).sort(
      (a, b) => a.event.startsAt - b.event.startsAt,
    ),
  );

  readonly eventsById = this.eventsSync.docs;

  // ------------------------------------------------------------- lottery

  /** The running programmes, by id. Most clubs have one. */
  readonly lotteriesById = this.lotteriesSync.docs;

  /** Every visible draw, newest first - the current one leads. */
  readonly lotteryDraws = computed(() =>
    Array.from(this.lotteryDrawsSync.docs(), ([drawId, draw]) => ({ drawId, draw })).sort(
      (a, b) => b.draw.drawSeq - a.draw.drawSeq,
    ),
  );

  /** My lines (and my family's), newest first. */
  readonly lotteryEntries = computed(() =>
    Array.from(this.lotteryEntriesSync.docs(), ([entryId, entry]) => ({ entryId, entry })).sort(
      (a, b) => b.entry.createdAt - a.entry.createdAt,
    ),
  );

  /** The event behind one of my tickets, when it is still published. */
  eventFor(eventId: string): ClubEvent | undefined {
    return this.eventsById().get(eventId);
  }

  /** My tickets (and my children's), newest first. */
  readonly tickets = computed(() =>
    Array.from(this.ticketsSync.docs(), ([ticketId, ticket]) => ({ ticketId, ticket })).sort(
      (a, b) => b.ticket.issuedAt - a.ticket.issuedAt,
    ),
  );

  /** The club's open asks addressed to me or mine, newest first. */
  readonly paymentRequests = computed(() =>
    Array.from(this.requestsSync.docs(), ([paymentRequestId, request]) => ({
      paymentRequestId,
      request,
    })).sort((a, b) => b.request.createdAt - a.request.createdAt),
  );

  /** My own ledger, newest first. */
  readonly transactions = computed(() =>
    Array.from(this.transactionsSync.docs(), ([transactionId, transaction]) => ({
      transactionId,
      transaction,
    })).sort((a, b) => b.transaction.date - a.transaction.date),
  );

  /** My memberships, with the `{planId}_{memberId}` key unpacked. */
  readonly registrations = computed<readonly MemberRegistrationEntry[]>(() =>
    // The key is `{seasonId}_{memberId}`; the plan is a FIELD on the doc
    // now, because a person changes plan without changing registration.
    Array.from(this.registrationsSync.docs(), ([key, registration]) => ({
      seasonId: key.slice(0, key.indexOf('_')),
      memberId: key.slice(key.indexOf('_') + 1),
      planId: registration.planId,
      registration,
    })),
  );
}
